import type { AxiosInstance } from "axios";
import type { QueryClient } from "@tanstack/react-query";
import { fetchMediaEntity } from "@/api/api";
import { prefetchMediaRefsBatch, type MediaBatchItemRef } from "@/services/mediaBatch";
import type { LibraryItem } from "./libraryItems";

const LIBRARY_MEDIA_PREFETCH_STALE_MS = 60_000;

/** Single-item prefetch (e.g. drag start) - not the N+1 case, a plain per-id fetch is fine here. */
export async function prefetchLibraryMediaAssets(
  queryClient: QueryClient,
  axiosClient: AxiosInstance,
  projectId: string,
  imageId: string,
  videoId?: string | null
): Promise<void> {
  const prefetches = [
    queryClient.prefetchQuery({
      queryKey: ["image", projectId, imageId],
      queryFn: async () => fetchMediaEntity(axiosClient, projectId, imageId, "image"),
      staleTime: LIBRARY_MEDIA_PREFETCH_STALE_MS
    })
  ];

  const trimmedVideoId = typeof videoId === "string" ? videoId.trim() : "";
  if (trimmedVideoId.length > 0) {
    prefetches.push(
      queryClient.prefetchQuery({
        queryKey: ["video", projectId, trimmedVideoId],
        queryFn: async () => fetchMediaEntity(axiosClient, projectId, trimmedVideoId, "video"),
        staleTime: LIBRARY_MEDIA_PREFETCH_STALE_MS
      })
    );
  }

  await Promise.all(prefetches);
}

/**
 * Warms the `[kind, projectId, id]` cache for every image/video a set of library items reference,
 * via one batched POST /{project}/media/batch call rather than one prefetch per item — refs already
 * fresh in the cache (e.g. already loaded by a mounted `useMediaEntity`) are skipped, same as
 * `queryClient.prefetchQuery`'s own staleTime check did before.
 */
export async function prefetchLibraryItemsMedia(
  queryClient: QueryClient,
  axiosClient: AxiosInstance,
  projectId: string,
  items: readonly LibraryItem[],
  completedVideoByImageId: ReadonlyMap<string, string>
): Promise<void> {
  const refs: MediaBatchItemRef[] = [];

  for (const item of items) {
    if (item.imageId.trim() === "") {
      continue;
    }

    const videoId = item.libraryVideoId ?? (item.section === "shots" ? (completedVideoByImageId.get(item.imageId) ?? null) : null);
    refs.push({ kind: "image", id: item.imageId });
    const trimmedVideoId = typeof videoId === "string" ? videoId.trim() : "";
    if (trimmedVideoId.length > 0) {
      refs.push({ kind: "video", id: trimmedVideoId });
    }
  }

  await prefetchMediaRefsBatch(queryClient, axiosClient, projectId, refs);
}
